import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { LoanAPIResponseMapper, LoanOverviewMapper } from '@/api/mapper/loan';
import { getLoan, getLoans, getPolicyLoans } from '@/api/service/loan';
import type { LoanListQuery } from '@/api/type/loan';

export const loanKeys = {
  all: ['loans'] as const,
  list: (query: LoanListQuery) => [...loanKeys.all, 'list', query] as const,
  detail: (loanId: string) => [...loanKeys.all, 'detail', loanId] as const,
  byPolicy: (policyId: string) => [...loanKeys.all, 'policy', policyId] as const,
};

/** One page of the Loans table, searched and filtered by the backend, with the page summary. */
export function useLoansQuery(query: LoanListQuery, enabled = true) {
  return useQuery({
    queryKey: loanKeys.list(query),
    queryFn: async () => {
      const response = await getLoans(query);
      return {
        loans: (response.data ?? []).map(LoanAPIResponseMapper),
        total: response.total ?? 0,
        overview: LoanOverviewMapper(response.summary),
      };
    },
    placeholderData: keepPreviousData,
    enabled,
  });
}

/** Loans given on one policy, for the Policy Loan drawer history. */
export function usePolicyLoansQuery(policyId?: string | null, enabled = true) {
  return useQuery({
    queryKey: loanKeys.byPolicy(policyId ?? 'none'),
    queryFn: async () => ((await getPolicyLoans(Number(policyId))).data ?? []).map(LoanAPIResponseMapper),
    enabled: enabled && Boolean(policyId),
  });
}

export function useLoanQuery(loanId?: string | null, enabled = true) {
  return useQuery({
    queryKey: loanKeys.detail(loanId ?? 'none'),
    queryFn: async () => LoanAPIResponseMapper(await getLoan(Number(loanId))),
    enabled: enabled && Boolean(loanId),
  });
}
